// ContractPage.jsx
import React, { useEffect, useState, useRef } from 'react';
import {
    Container,
    Row,
    Col,
    Card,
    CardBody,
    Button,
    Form,
    FormGroup,
    Label,
    Input,
    Alert
} from 'reactstrap';
import { Link } from 'react-router-dom';
import './ContractPage.scss';

const ContractPage = () => {
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);
    const contractRef = useRef(null);
    const [formData, setFormData] = useState({
        clientName: '',
        clientCompany: '',
        clientEmail: '',
        clientPhone: '',
        clientAddress: '',
        serviceCategory: 'seo',
        specificServices: [],
        contractStartDate: new Date().toISOString().split('T')[0],
        contractDuration: '12',
        monthlyBudget: '',
        additionalNotes: ''
    });

    const [submitted, setSubmitted] = useState(false);
    const [generatedContract, setGeneratedContract] = useState(null);

    const serviceOptions = [
        { id: 'seo', name: 'SEO Services', icon: 'fas fa-search', subServices: ['AI SEO', 'GEO', 'Technical SEO', 'Ecommerce SEO', 'International SEO'] },
        { id: 'paid-media', name: 'Paid Media', icon: 'fas fa-ad', subServices: ['PPC', 'Paid Social', 'Programmatic', 'Meta Advertising', 'PPC Consultancy'] },
        { id: 'data-ai', name: 'Data & AI', icon: 'fas fa-database', subServices: ['AI & Automation', 'Analytics', 'Tag Management'] },
        { id: 'strategy', name: 'Strategy', icon: 'fas fa-chess', subServices: ['Audits & Consultancy', 'Market Research', 'Media Planning', 'In-housing', 'International Marketing'] },
        { id: 'experience', name: 'Experience', icon: 'fas fa-palette', subServices: ['Creative', 'CRO', 'Technology', 'Video'] }
    ];

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleServiceSelection = (serviceId) => {
        setFormData(prev => {
            const current = [...prev.specificServices];
            if (current.includes(serviceId)) {
                return { ...prev, specificServices: current.filter(id => id !== serviceId) };
            } else {
                return { ...prev, specificServices: [...current, serviceId] };
            }
        });
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setGeneratedContract(formData);
        setSubmitted(true);
        // Scroll to contract preview
        setTimeout(() => {
            document.getElementById('contract-preview')?.scrollIntoView({ behavior: 'smooth' });
        }, 100);
    };

    const handleDownloadPDF = () => {
        const printContent = contractRef.current.innerHTML;
        const originalTitle = document.title;
        document.title = `Reachtix_Contract_${formData.clientName.replace(/\s/g, '_')}`;

        const printWindow = window.open('', '_blank');
        printWindow.document.write(`
            <!DOCTYPE html>
            <html>
            <head>
                <title>Reachtix Service Contract</title>
                <meta charset="UTF-8">
                <style>
                    * {
                        margin: 0;
                        padding: 0;
                        box-sizing: border-box;
                    }
                    body {
                        font-family: 'Inter', 'Segoe UI', Arial, sans-serif;
                        line-height: 1.5;
                        color: #1a1a1a;
                        padding: 40px;
                        background: white;
                    }
                    .contract-container {
                        max-width: 1000px;
                        margin: 0 auto;
                        background: white;
                    }
                    .header {
                        text-align: center;
                        margin-bottom: 30px;
                        padding-bottom: 20px;
                        border-bottom: 3px solid #EEBA00;
                    }
                    .logo {
                        font-size: 28px;
                        font-weight: bold;
                        color: #EEBA00;
                        margin-bottom: 10px;
                    }
                    .title {
                        font-size: 24px;
                        font-weight: bold;
                        margin: 20px 0 10px;
                    }
                    .contract-number {
                        color: #666;
                        font-size: 12px;
                    }
                    .section {
                        margin-bottom: 25px;
                    }
                    .section-title {
                        font-size: 18px;
                        font-weight: bold;
                        background: #f5f5f5;
                        padding: 8px 12px;
                        margin-bottom: 15px;
                        border-left: 4px solid #EEBA00;
                    }
                    .info-grid {
                        display: grid;
                        grid-template-columns: 1fr 1fr;
                        gap: 15px;
                        margin-bottom: 15px;
                    }
                    .info-item {
                        margin-bottom: 10px;
                    }
                    .info-label {
                        font-weight: bold;
                        font-size: 12px;
                        text-transform: uppercase;
                        color: #666;
                        margin-bottom: 3px;
                    }
                    .info-value {
                        font-size: 14px;
                    }
                    table {
                        width: 100%;
                        border-collapse: collapse;
                        margin: 15px 0;
                    }
                    th, td {
                        border: 1px solid #ddd;
                        padding: 10px;
                        text-align: left;
                    }
                    th {
                        background: #f5f5f5;
                        font-weight: bold;
                    }
                    .signature-area {
                        margin-top: 40px;
                        display: grid;
                        grid-template-columns: 1fr 1fr;
                        gap: 30px;
                    }
                    .signature-line {
                        margin-top: 30px;
                        border-top: 1px solid #333;
                        padding-top: 8px;
                        font-size: 12px;
                    }
                    .footer {
                        margin-top: 40px;
                        padding-top: 20px;
                        border-top: 1px solid #ddd;
                        font-size: 10px;
                        color: #666;
                        text-align: center;
                    }
                    @media print {
                        body {
                            padding: 20px;
                        }
                        .no-print {
                            display: none;
                        }
                    }
                </style>
            </head>
            <body>
                <div class="contract-container">
                    ${printContent}
                </div>
                <script>
                    window.onload = () => {
                        window.print();
                        setTimeout(() => window.close(), 1000);
                    };
                <\/script>
            </body>
            </html>
        `);
        printWindow.document.close();
        document.title = originalTitle;
    };

    const getSelectedServicesNames = () => {
        const selected = [];
        serviceOptions.forEach(category => {
            category.subServices.forEach(service => {
                const serviceId = `${category.id}-${service.replace(/\s+/g, '-').toLowerCase()}`;
                if (formData.specificServices.includes(serviceId)) {
                    selected.push(`${service} (${category.name})`);
                }
            });
        });
        return selected;
    };

    const getServiceCategoryName = (id) => {
        const category = serviceOptions.find(c => c.id === id);
        return category ? category.name : id;
    };

    return (
        <div className="contract-page">
            {/* Hero Section */}
            <section className="contract-hero-section">
                <Container>
                    <Row className="text-center">
                        <Col lg={8} className="mx-auto">
                            <h1 className="contract-hero-title">
                                Service Contract Agreement
                            </h1>
                            <p className="contract-hero-subtitle">
                                Complete this form to generate a formal service contract between you and Reachtix.
                                Review the terms, fill in your details, and download the contract as PDF.
                            </p>
                        </Col>
                    </Row>
                </Container>
            </section>

            <Container className="py-5">
                <Row>
                    {/* Form Column */}
                    <Col lg={6} className={submitted ? 'd-none d-lg-block' : ''}>
                        <Card className="contract-form-card">
                            <CardBody className="p-4">
                                <h3 className="form-title mb-4">Contract Details</h3>
                                <Form onSubmit={handleSubmit}>
                                    <FormGroup>
                                        <Label for="clientName">Full Name *</Label>
                                        <Input
                                            type="text"
                                            id="clientName"
                                            name="clientName"
                                            value={formData.clientName}
                                            onChange={handleInputChange}
                                            required
                                            placeholder="John Smith"
                                        />
                                    </FormGroup>

                                    <FormGroup>
                                        <Label for="clientCompany">Company Name *</Label>
                                        <Input
                                            type="text"
                                            id="clientCompany"
                                            name="clientCompany"
                                            value={formData.clientCompany}
                                            onChange={handleInputChange}
                                            required
                                            placeholder="Company Ltd"
                                        />
                                    </FormGroup>

                                    <FormGroup>
                                        <Label for="clientEmail">Email Address *</Label>
                                        <Input
                                            type="email"
                                            id="clientEmail"
                                            name="clientEmail"
                                            value={formData.clientEmail}
                                            onChange={handleInputChange}
                                            required
                                            placeholder="contact@company.com"
                                        />
                                    </FormGroup>

                                    <FormGroup>
                                        <Label for="clientPhone">Phone Number</Label>
                                        <Input
                                            type="tel"
                                            id="clientPhone"
                                            name="clientPhone"
                                            value={formData.clientPhone}
                                            onChange={handleInputChange}
                                            placeholder="+44 20 1234 5678"
                                        />
                                    </FormGroup>

                                    <FormGroup>
                                        <Label for="clientAddress">Business Address *</Label>
                                        <Input
                                            type="text"
                                            id="clientAddress"
                                            name="clientAddress"
                                            value={formData.clientAddress}
                                            onChange={handleInputChange}
                                            required
                                            placeholder="330 High Holborn, London, WC1V 7QH"
                                        />
                                    </FormGroup>

                                    <FormGroup>
                                        <Label for="serviceCategory">Primary Service Category *</Label>
                                        <Input
                                            type="select"
                                            id="serviceCategory"
                                            name="serviceCategory"
                                            value={formData.serviceCategory}
                                            onChange={handleInputChange}
                                            required
                                        >
                                            {serviceOptions.map(option => (
                                                <option key={option.id} value={option.id}>{option.name}</option>
                                            ))}
                                        </Input>
                                    </FormGroup>

                                    <FormGroup>
                                        <Label>Specific Services Required *</Label>
                                        <div className="service-checkboxes">
                                            {serviceOptions.map(category => (
                                                <div key={category.id} className="service-category-group mb-3">
                                                    <div className="category-header">
                                                        <i className={`${category.icon} me-2`}></i>
                                                        <strong>{category.name}</strong>
                                                    </div>
                                                    <div className="sub-services-list">
                                                        {category.subServices.map(service => {
                                                            const serviceId = `${category.id}-${service.replace(/\s+/g, '-').toLowerCase()}`;
                                                            return (
                                                                <Label key={serviceId} check className="service-checkbox">
                                                                    <Input
                                                                        type="checkbox"
                                                                        checked={formData.specificServices.includes(serviceId)}
                                                                        onChange={() => handleServiceSelection(serviceId)}
                                                                    />
                                                                    <span>{service}</span>
                                                                </Label>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </FormGroup>

                                    <Row>
                                        <Col md={6}>
                                            <FormGroup>
                                                <Label for="contractStartDate">Contract Start Date *</Label>
                                                <Input
                                                    type="date"
                                                    id="contractStartDate"
                                                    name="contractStartDate"
                                                    value={formData.contractStartDate}
                                                    onChange={handleInputChange}
                                                    required
                                                />
                                            </FormGroup>
                                        </Col>
                                        <Col md={6}>
                                            <FormGroup>
                                                <Label for="contractDuration">Contract Duration (months) *</Label>
                                                <Input
                                                    type="select"
                                                    id="contractDuration"
                                                    name="contractDuration"
                                                    value={formData.contractDuration}
                                                    onChange={handleInputChange}
                                                    required
                                                >
                                                    <option value="3">3 months</option>
                                                    <option value="6">6 months</option>
                                                    <option value="12">12 months</option>
                                                    <option value="24">24 months</option>
                                                </Input>
                                            </FormGroup>
                                        </Col>
                                    </Row>

                                    <FormGroup>
                                        <Label for="monthlyBudget">Estimated Monthly Budget (GBP)</Label>
                                        <Input
                                            type="text"
                                            id="monthlyBudget"
                                            name="monthlyBudget"
                                            value={formData.monthlyBudget}
                                            onChange={handleInputChange}
                                            placeholder="e.g., £2,500 - £5,000"
                                        />
                                    </FormGroup>

                                    <FormGroup>
                                        <Label for="additionalNotes">Additional Notes / Special Requirements</Label>
                                        <Input
                                            type="textarea"
                                            id="additionalNotes"
                                            name="additionalNotes"
                                            value={formData.additionalNotes}
                                            onChange={handleInputChange}
                                            rows="3"
                                            placeholder="Any specific requirements or information for Reachtix..."
                                        />
                                    </FormGroup>

                                    <Alert color="info" className="mt-3">
                                        <i className="fas fa-info-circle me-2"></i>
                                        By submitting this form, you agree to the terms and conditions of service.
                                        A formal contract will be generated for your review.
                                    </Alert>

                                    <Button type="submit" className="btn-generate-contract w-100">
                                        <i className="fas fa-file-contract me-2"></i>
                                        Generate Contract
                                    </Button>
                                </Form>
                            </CardBody>
                        </Card>
                    </Col>

                    {/* Contract Preview Column */}
                    <Col lg={6}>
                        <div className="contract-preview-wrapper" id="contract-preview">
                            {submitted && generatedContract ? (
                                <>
                                    <div className="contract-actions no-print mb-3">
                                        <Button className="btn-download-pdf" onClick={handleDownloadPDF}>
                                            <i className="fas fa-download me-2"></i>
                                            Download as PDF
                                        </Button>
                                        <Button className="btn-print" onClick={() => window.print()}>
                                            <i className="fas fa-print me-2"></i>
                                            Print Contract
                                        </Button>
                                    </div>
                                    <div className="contract-content" ref={contractRef}>
                                        {/* Contract Header */}
                                        <div className="contract-header">
                                            <div className="logo">REACHTIX</div>
                                            <div className="title">SERVICE CONTRACT AGREEMENT</div>
                                            <div className="contract-number">
                                                Contract No: RCT-{new Date().getFullYear()}-{Math.floor(Math.random() * 10000)}
                                            </div>
                                            <div className="contract-date">
                                                Date: {new Date().toLocaleDateString('en-GB')}
                                            </div>
                                        </div>

                                        {/* Parties */}
                                        <div className="section">
                                            <div className="section-title">1. PARTIES TO THE AGREEMENT</div>
                                            <div className="info-grid">
                                                <div>
                                                    <div className="info-label">Service Provider</div>
                                                    <div className="info-value">
                                                        <strong>QUANTUMCREST SOLUTIONS LTD</strong><br />
                                                        Trading as Reachtix<br />
                                                        330 High Holborn, London, WC1V 7QH<br />
                                                        United Kingdom<br />
                                                        Company No: 16088299
                                                    </div>
                                                </div>
                                                <div>
                                                    <div className="info-label">Client</div>
                                                    <div className="info-value">
                                                        <strong>{generatedContract.clientName}</strong><br />
                                                        {generatedContract.clientCompany}<br />
                                                        {generatedContract.clientAddress}<br />
                                                        {generatedContract.clientEmail}<br />
                                                        {generatedContract.clientPhone && `Tel: ${generatedContract.clientPhone}`}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Services */}
                                        <div className="section">
                                            <div className="section-title">2. SCOPE OF SERVICES</div>
                                            <p>The Service Provider agrees to provide the following digital marketing services to the Client:</p>
                                            <table>
                                                <thead>
                                                    <tr><th>Service Category</th><th>Primary Service</th><th>Specific Services</th></tr>
                                                </thead>
                                                <tbody>
                                                    <tr>
                                                        <td>{getServiceCategoryName(generatedContract.serviceCategory)}</td>
                                                        <td>{getServiceCategoryName(generatedContract.serviceCategory)}</td>
                                                        <td><ul style={{ margin: 0, paddingLeft: '20px' }}>
                                                            {getSelectedServicesNames().map((s, i) => <li key={i}>{s}</li>)}
                                                            {getSelectedServicesNames().length === 0 && <li>To be specified in Statement of Work</li>}
                                                        </ul></td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                            {generatedContract.additionalNotes && (
                                                <p><strong>Special Requirements:</strong> {generatedContract.additionalNotes}</p>
                                            )}
                                        </div>

                                        {/* Term */}
                                        <div className="section">
                                            <div className="section-title">3. CONTRACT TERM</div>
                                            <p>
                                                This Agreement shall commence on <strong>{new Date(generatedContract.contractStartDate).toLocaleDateString('en-GB')}</strong>
                                                and continue for an initial term of <strong>{generatedContract.contractDuration} months</strong>.
                                                Thereafter, the Agreement shall automatically renew on a month-to-month basis unless terminated
                                                by either party with 30 days' written notice.
                                            </p>
                                        </div>

                                        {/* Fees */}
                                        <div className="section">
                                            <div className="section-title">4. FEES & PAYMENT</div>
                                            <p>
                                                <strong>Management Fees:</strong> As detailed in the applicable Statement of Work.<br />
                                                <strong>Advertising Spend:</strong> Client is responsible for all advertising costs on third-party platforms.<br />
                                                {generatedContract.monthlyBudget && (
                                                    <><strong>Estimated Monthly Budget:</strong> {generatedContract.monthlyBudget}<br /></>
                                                )}
                                                <strong>Payment Terms:</strong> Invoices are due within 14 days of receipt.
                                                Late payments may incur interest at 8% per annum above the Bank of England base rate.
                                            </p>
                                        </div>

                                        {/* Key Terms */}
                                        <div className="section">
                                            <div className="section-title">5. KEY TERMS & CONDITIONS</div>
                                            <p><strong>5.1 Service Delivery:</strong> Reachtix shall provide the Services with reasonable skill and care.</p>
                                            <p><strong>5.2 Client Obligations:</strong> Client shall provide timely access, information, and approvals as reasonably required.</p>
                                            <p><strong>5.3 Intellectual Property:</strong> All deliverables remain the property of Reachtix until full payment is received.</p>
                                            <p><strong>5.4 Confidentiality:</strong> Both parties agree to keep confidential information secure.</p>
                                            <p><strong>5.5 Limitation of Liability:</strong> Total liability shall not exceed fees paid in the preceding 12 months.</p>
                                            <p><strong>5.6 Governing Law:</strong> This Agreement is governed by the laws of England and Wales.</p>
                                        </div>

                                        {/* Termination */}
                                        <div className="section">
                                            <div className="section-title">6. TERMINATION</div>
                                            <p>
                                                Either party may terminate this Agreement:
                                                (a) with 30 days' written notice;
                                                (b) immediately if the other party commits a material breach that remains uncured for 14 days;
                                                (c) immediately if the other party becomes insolvent or ceases trading.
                                            </p>
                                        </div>

                                        {/* Signatures */}
                                        <div className="signature-area">
                                            <div>
                                                <div className="signature-line">
                                                    _____________________________<br />
                                                    Signature (Client)<br />
                                                    {generatedContract.clientName}<br />
                                                    Date: {new Date().toLocaleDateString('en-GB')}
                                                </div>
                                            </div>
                                            <div>
                                                <div className="signature-line">
                                                    _____________________________<br />
                                                    Signature (Reachtix)<br />
                                                    For and on behalf of QUANTUMCREST SOLUTIONS LTD<br />
                                                    Date: {new Date().toLocaleDateString('en-GB')}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Footer */}
                                        <div className="footer">
                                            <p>Reachtix is a trading name of QUANTUMCREST SOLUTIONS LTD | Company No: 16088299</p>
                                            <p>330 High Holborn, London, WC1V 7QH, United Kingdom</p>
                                            <p>This Contract is subject to Reachtix's standard Terms and Conditions available at www.reachtix.com/terms</p>
                                        </div>
                                    </div>
                                </>
                            ) : (
                                <Card className="contract-placeholder-card">
                                    <CardBody className="text-center p-5">
                                        <i className="fas fa-file-contract placeholder-icon"></i>
                                        <h4 className="mt-3">Contract Preview</h4>
                                        <p className="text-muted">
                                            Complete the form on the left to generate your service contract.
                                            The contract will appear here for review and download.
                                        </p>
                                        <div className="placeholder-steps">
                                            <div className="step"><i className="fas fa-check-circle"></i> Fill in your details</div>
                                            <div className="step"><i className="fas fa-check-circle"></i> Select required services</div>
                                            <div className="step"><i className="fas fa-check-circle"></i> Generate & download PDF</div>
                                        </div>
                                    </CardBody>
                                </Card>
                            )}
                        </div>
                    </Col>
                </Row>
            </Container>

            {/* Trust Section */}
            <section className="contract-trust-section">
                <Container>
                    <Row className="text-center">
                        <Col md={3} sm={6} className="mb-4">
                            <i className="fas fa-lock fa-2x"></i>
                            <h5>Secure & Binding</h5>
                            <p className="small text-muted">Legally binding contract under UK law</p>
                        </Col>
                        <Col md={3} sm={6} className="mb-4">
                            <i className="fas fa-file-pdf fa-2x"></i>
                            <h5>PDF Download</h5>
                            <p className="small text-muted">Download and print your contract</p>
                        </Col>
                        <Col md={3} sm={6} className="mb-4">
                            <i className="fas fa-gavel fa-2x"></i>
                            <h5>GDPR Compliant</h5>
                            <p className="small text-muted">Full compliance with UK data protection laws</p>
                        </Col>
                        <Col md={3} sm={6} className="mb-4">
                            <i className="fas fa-headset fa-2x"></i>
                            <h5>Support Available</h5>
                            <p className="small text-muted">Contact us with any questions</p>
                        </Col>
                    </Row>
                </Container>
            </section>
        </div>
    );
};

export default ContractPage;